import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { OptimizelyProvider, ReactSDKClient, createInstance, setLogLevel } from '@optimizely/react-sdk';

const OptimizelyContext = createContext<{
    optimizelyClient: ReactSDKClient;
    forceUpdateOptimizelyClient: () => void;
    isAccrueRedeemMWEnabled: boolean;
    isChoiceAccrualUiEnabled: boolean;
    isLoading: boolean;
}>({
    optimizelyClient: null as unknown as ReactSDKClient,
    forceUpdateOptimizelyClient: () => { },
    isAccrueRedeemMWEnabled: false,
    isChoiceAccrualUiEnabled: false,
    isLoading: true
});

export const useOptimizelyContext = () => {
    return useContext(OptimizelyContext);
};

const OptimizelyContextProvider = (
    { children, userId, mileagePlanNumber }: { children: ReactNode, userId: string, mileagePlanNumber: string }
) => {
    const [optimizelyClient, setOptimizelyClient] = useState<ReactSDKClient>(null as unknown as ReactSDKClient);
    const [isAccrueRedeemMWEnabled, setIsAccrueRedeemMWEnabled] = useState(false);
    const [isChoiceAccrualUiEnabled, setIsChoiceAccrualUiEnabled] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const createOptimizelyClient = useCallback(() => {
        const client = createInstance({
            sdkKey: process.env.REACT_APP_OPTIMIZELY_SDK_KEY,
        });
        client.setUser({
            id: userId,
            attributes: {
                mileagePlanNumber: mileagePlanNumber
            }
        });
        setLogLevel('debug');
        setOptimizelyClient(client);
    }, [userId, mileagePlanNumber]);

    useEffect(() => {
        if (!!userId && !!mileagePlanNumber) {
            createOptimizelyClient();
        }
    }, [userId, mileagePlanNumber, createOptimizelyClient]);

    //
    // Fetch feature flag
    useEffect(() => {
        const fetchFlags = async () => {
            await optimizelyClient.onReady();

            const enabled = optimizelyClient?.isFeatureEnabled(
                'activities_web_ui_accrueredeemmw_toggle'
            ) ?? false;

            const choiceAccrualUiEnabled = optimizelyClient?.isFeatureEnabled(
                'choice_accrual_ui_enabled'
            ) ?? false;

            setIsAccrueRedeemMWEnabled(enabled);
            setIsChoiceAccrualUiEnabled(choiceAccrualUiEnabled);
            setIsLoading(false);
        }

        if (optimizelyClient) {
            fetchFlags();
        }
    }, [optimizelyClient]);


    if (!optimizelyClient) return null;

    return (
        <OptimizelyContext.Provider value={{
            optimizelyClient: optimizelyClient,
            forceUpdateOptimizelyClient: createOptimizelyClient,
            isAccrueRedeemMWEnabled,
            isChoiceAccrualUiEnabled,
            isLoading
        }}>
            <OptimizelyProvider
                optimizely={optimizelyClient}
                userId={userId}
                userAttributes={{mileagePlanNumber: mileagePlanNumber}}>
                {children}
            </OptimizelyProvider>
        </OptimizelyContext.Provider>
    );
};

export default OptimizelyContextProvider;